'use client';
import dynamic from 'next/dynamic';

import { BotAvatar } from '~components/BotAvatar';
import { IconButton } from '~components/IconButton';
import { P } from '~components/Typography';
import { routes } from '~constants/routes';
import { useDialogs } from '~entities/dialogs';
import { annals } from '~features/annals/hooks';
import { useCurrentBot } from '~hooks/useCurrentBot';
import { useCustomChatInputEventListener } from '~hooks/useCustomChatInputEvent';
import { useSession } from '~hooks/useSession';
import { useRouter } from '~i18n/navigation';
import {
	Level as LogLevel,
	Logger,
	LoggerMessages,
	LoggerServices,
} from '~shared/services/logger';
import { UnreadMessagesBadge } from '~shared/ui/atoms';

import ArrowBack from '../../../../../public/assets/icons/arrowBack.svg';
import TickSimple from '../../../../../public/assets/icons/tick-simple.svg';
import {
	BackButtonWrapper,
	MessageListContent,
	MessageListHeaderOuter,
	unreadMessagesClass,
} from './styles';
type TMessageListHeaderProps = {
	recipientId: string;
};

const HeaderNonAuthContent = dynamic(
	() =>
		import('../../../../components/Header/NonAuthContent').then(
			(mod) => mod.HeaderNonAuthContent
		),
	{ ssr: false }
);
const AuthContent = dynamic(
	() => import('../AuthContent').then((mod) => mod.AuthContent),
	{ ssr: false }
);

export const MessageListHeader = ({ recipientId }: TMessageListHeaderProps) => {
	const { bot, botSuccess } = useCurrentBot({ recipientId });
	const { isDemoUser } = useSession();
	const type = bot?.accountType ?? '';
	const botName = bot?.name ?? '';
	const botClientId = bot?.clientId ?? '';

	if (botSuccess && (!bot || !bot.name || !bot.clientId)) {
		Logger.shared.log(
			LogLevel.warn,
			LoggerServices.ChatService,
			LoggerMessages.NoBotDataInMessageList,
			{ recipient: recipientId, bot }
		);
	}

	const router = useRouter();
	const { unreadMessagesCount } = useDialogs();

	const annalsBackToChatList = annals.chat.useBackToChatList({
		unreadCount: unreadMessagesCount,
		botName,
		botClientId,
	});

	const handleGoBack = () => {
		void annalsBackToChatList();
		router.replace(routes.chats.home);
	};

	const inputFocused = useCustomChatInputEventListener();

	if (inputFocused) {
		return (
			<BackButtonWrapper>
				<IconButton onClick={handleGoBack}>
					<ArrowBack />
				</IconButton>
			</BackButtonWrapper>
		);
	}

	return (
		<MessageListHeaderOuter>
			<IconButton onClick={handleGoBack}>
				<ArrowBack />
				{unreadMessagesCount > 0 && (
					<UnreadMessagesBadge
						className={unreadMessagesClass}
						count={unreadMessagesCount}
					/>
				)}
			</IconButton>
			<BotAvatar
				item={bot?.images?.chatAvatarLinks}
				fallback={bot?.images?.chatAvatarPath ?? undefined}
				size={56}
			/>
			<MessageListContent>
				{botSuccess && (
					<P size="large" align="left" weight={500}>
						{bot?.name}
						{type === 'influencer' && <TickSimple />}
					</P>
				)}
			</MessageListContent>
			{isDemoUser ? <HeaderNonAuthContent /> : <AuthContent />}
		</MessageListHeaderOuter>
	);
};
